/**
 * StoreBadges — official-style App Store + Play Store badges as
 * inline SVGs. Used by the PaywallSheet "Subscribe in the app"
 * surface, the /get smart-redirect page, and the landing hero so all
 * three stay in lockstep.
 *
 * Both badges follow the dark variant of the brand guidelines (black
 * rounded rectangle, white iconography + text). Inline SVGs (not
 * official PNG/SVG downloads) so we don't carry binary assets, and
 * so the badges scale crisply at every density without an asset
 * pipeline. Drawn close enough to Apple/Google's official versions
 * to be instantly recognisable; not a perfect pixel match — the
 * brand teams care more about colour + lockup than sub-pixel kerning.
 *
 * The corner radius is rx=20 — a full pill at the 40-unit viewBox
 * height — per the Claude Design source of truth (WYDM Landing.html
 * hero). Because these components are shared, that pill shape also
 * applies to the PaywallSheet and /get surfaces.
 *
 * Hrefs are wired via the centralised constants in lib/stores.ts;
 * both listings are live. The anchors open in a new tab so a click
 * from a marketing surface doesn't discard the page — on iOS/Android
 * the store link hands off to the native store app regardless, so
 * the target is a no-op there.
 */

interface BadgeProps {
  href: string;
  // Visual height in pixels. Width is derived from the SVG viewBox
  // aspect ratio so the badge stays proportional.
  height?: number;
  // Optional aria-label override. Defaults sensible for screen
  // readers; only override if the badge has unusual context.
  ariaLabel?: string;
}

/**
 * AppStoreBadge — "Download on the App Store" (dark variant).
 * SVG viewBox 120 × 40 → default height 44 → width ≈ 132.
 */
export function AppStoreBadge({ href, height = 44, ariaLabel = 'Download on the App Store' }: BadgeProps) {
  return (
    <a
      href={href}
      aria-label={ariaLabel}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        textDecoration: 'none',
        lineHeight: 0, // strip baseline gap so the SVG sits flush
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 120 40"
        height={height}
        role="img"
        aria-hidden="true"
      >
        {/* Background — rounded rectangle, brand black. Apple guidelines
            allow a subtle white outline for use on dark backgrounds; we
            sit on var(--bg) (cream/white) so no outline needed. */}
        <rect width="120" height="40" rx="20" fill="#000" />

        {/* Apple logo — left side, vertically centred. Path from
            Apple's brand resources, simplified to a single d-string. */}
        <g transform="translate(10, 8) scale(0.85)">
          <path
            fill="#fff"
            d="M19.5 14.6c0-3.3 2.7-4.9 2.8-5-1.5-2.2-3.9-2.5-4.7-2.6-2-.2-3.9 1.2-4.9 1.2-1 0-2.6-1.2-4.3-1.1-2.2 0-4.2 1.3-5.4 3.3-2.3 4-.6 9.9 1.7 13.1 1.1 1.6 2.4 3.4 4.1 3.3 1.7-.1 2.3-1.1 4.3-1.1 2 0 2.6 1.1 4.3 1 1.8 0 2.9-1.6 4-3.2 1.3-1.8 1.8-3.7 1.8-3.8-.1-.1-3.5-1.4-3.5-5.1zm-3.2-9.4c.9-1.1 1.5-2.6 1.3-4.2-1.3 0-2.9.9-3.8 2-.8 1-1.6 2.5-1.4 4 1.5.1 3-.7 3.9-1.8z"
          />
        </g>

        {/* "Download on the" — tiny label, top line. */}
        <text
          x="38"
          y="16"
          fill="#fff"
          fontFamily="-apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif"
          fontSize="7.5"
          letterSpacing="0.05"
        >
          Download on the
        </text>

        {/* "App Store" — wordmark, larger. */}
        <text
          x="38"
          y="30"
          fill="#fff"
          fontFamily="-apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif"
          fontSize="14"
          fontWeight="600"
          letterSpacing="-0.4"
        >
          App Store
        </text>
      </svg>
    </a>
  );
}

/**
 * PlayStoreBadge — "Get it on Google Play" (dark variant).
 * SVG viewBox 135 × 40 → default height 44 → width ≈ 148.
 */
export function PlayStoreBadge({ href, height = 44, ariaLabel = 'Get it on Google Play' }: BadgeProps) {
  return (
    <a
      href={href}
      aria-label={ariaLabel}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        textDecoration: 'none',
        lineHeight: 0,
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 135 40"
        height={height}
        role="img"
        aria-hidden="true"
      >
        {/* Background — rounded rectangle, brand black. */}
        <rect width="135" height="40" rx="20" fill="#000" />

        {/* Play triangle — Google's four-colour wordmark icon.
            Built from four overlapping triangles whose shared vertex
            sits at the centre of the icon. Coordinates come from
            Google's official Play Store asset (simplified). */}
        <g transform="translate(10, 9) scale(0.95)">
          {/* Blue (left edge) */}
          <path
            fill="#00C3F2"
            d="M0.5 0.5 L11 11 L0.5 21.5 C0.2 21.2 0 20.7 0 20.2 V1.8 C0 1.3 0.2 0.8 0.5 0.5 Z"
          />
          {/* Green (top-right) */}
          <path
            fill="#00E676"
            d="M0.5 0.5 C0.9 0.1 1.5 0 2 0.3 L14.8 7.6 L11 11 Z"
          />
          {/* Red (bottom-right) */}
          <path
            fill="#FF3D00"
            d="M0.5 21.5 L11 11 L14.8 14.4 L2 21.7 C1.5 22 0.9 21.9 0.5 21.5 Z"
          />
          {/* Yellow (right edge) */}
          <path
            fill="#FFC107"
            d="M14.8 7.6 L18.5 9.7 C19.2 10.1 19.2 11.9 18.5 12.3 L14.8 14.4 L11 11 Z"
          />
        </g>

        {/* "GET IT ON" — small caps, top line. */}
        <text
          x="42"
          y="16"
          fill="#fff"
          fontFamily="Roboto, 'Helvetica Neue', sans-serif"
          fontSize="7"
          letterSpacing="0.5"
        >
          GET IT ON
        </text>

        {/* "Google Play" — wordmark, larger. */}
        <text
          x="42"
          y="30"
          fill="#fff"
          fontFamily="Roboto, 'Helvetica Neue', sans-serif"
          fontSize="15"
          fontWeight="500"
          letterSpacing="-0.3"
        >
          Google Play
        </text>
      </svg>
    </a>
  );
}

/**
 * StoreBadgeRow — convenience grouping for the common pattern of
 * showing both badges stacked or side-by-side. Defaults to a
 * column on narrow widths (badges stack), row on wide.
 */
export function StoreBadgeRow({
  appStoreHref,
  playStoreHref,
  direction = 'column',
  height = 44,
}: {
  appStoreHref: string;
  playStoreHref: string;
  direction?: 'row' | 'column';
  height?: number;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: direction,
        gap: 10,
        alignItems: direction === 'column' ? 'stretch' : 'center',
      }}
    >
      <AppStoreBadge href={appStoreHref} height={height} />
      <PlayStoreBadge href={playStoreHref} height={height} />
    </div>
  );
}
